import React, { useState, useEffect, lazy, Suspense } from 'react';
import './index.css';
import { AuthProvider, useAuth } from './AuthContext';
import Nav from './Nav';
import Landing from './Landing';
// Profile.js is already statically bundled (Landing imports from it); keep static
import ProfilePage, { PolicyPage, PRIVACY, TERMS, DISCLAIMER } from './Profile';
// DeleteData is already bundled via Landing.js; keep static
import DeleteData from './DeleteData';

// Authenticated-only screens — not reachable on first load, safe to split into separate chunks
const Home = lazy(() => import('./Home'));
const CreateScript = lazy(() => import('./CreateScript'));
const ScriptView = lazy(() => import('./ScriptView'));
const Library = lazy(() => import('./Library'));
const LearnEFT = lazy(() => import('./LearnEFT'));
const PaymentSuccess = lazy(() => import('./PaymentSuccess'));
const ResetPassword = lazy(() => import('./ResetPassword'));
const QuickScript = lazy(() => import('./QuickScript'));

const PageSpinner = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div className="spinner" />
  </div>
);

const POLICY_ROUTES = {
  '/privacy':    { title: 'Privacy Policy',    content: PRIVACY },
  '/terms':      { title: 'Terms of Service',  content: TERMS },
  '/disclaimer': { title: 'Disclaimer',        content: DISCLAIMER },
};

function getResetToken() {
  return new URLSearchParams(window.location.search).get('reset_token');
}

function AppInner() {
  const { user, loading } = useAuth();
  const [page, setPage] = useState(getResetToken() ? 'resetpassword' : 'home');
  const [viewScript, setViewScript] = useState(null);
  const [resetToken] = useState(getResetToken);

  const policyRoute = POLICY_ROUTES[window.location.pathname];
  if (policyRoute) {
    return <PolicyPage title={policyRoute.title} content={policyRoute.content} onBack={() => { window.location.href = '/'; }} />;
  }

  if (window.location.pathname === '/delete-data') {
    return <DeleteData onBack={() => { window.location.href = '/'; }} />;
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner" />
      </div>
    );
  }

  const navigate = (p) => {
    setPage(p);
    window.scrollTo(0, 0);
  };

  const renderPage = () => {
    if (!user && page === 'home') return <Landing setPage={navigate} />;
    if (!user && page === 'login') return <Landing setPage={navigate} showLogin={true} />;

    if (page === 'scriptview' && viewScript) {
      return <ScriptView script={viewScript} setPage={navigate}
        onNewScript={() => { setViewScript(null); navigate('create'); }} />;
    }

    if (page === 'success') return <PaymentSuccess setPage={navigate} />;
    if (page === 'resetpassword' && resetToken) return <ResetPassword token={resetToken} setPage={navigate} />;

    switch (page) {
      case 'home': return user ? <Home setPage={navigate} /> : <Landing setPage={navigate} />;
      case 'create': return <CreateScript setPage={navigate} />;
      case 'quickscript': return <QuickScript setPage={navigate} />;
      case 'library': return <Library setPage={navigate} setViewScript={setViewScript} />;
      case 'learn': return <LearnEFT setPage={navigate} />;
      case 'profile': return user ? <ProfilePage setPage={navigate} /> : <Landing setPage={navigate} />;
      default: return user ? <Home setPage={navigate} /> : <Landing setPage={navigate} />;
    }
  };

  return (
    <>
      <Nav page={page} setPage={navigate} />
      <Suspense fallback={<PageSpinner />}>
        {renderPage()}
      </Suspense>
    </>
  );
}

function useInAppUpdate() {
  useEffect(() => {
    // Only run on Android native — silently skip on web
    const isNative = () => { try { return window.Capacitor?.isNativePlatform(); } catch { return false; } };
    if (!isNative()) return;

    let listener = null;

    const checkUpdate = async () => {
      try {
        const { AppUpdate, AppUpdateAvailability, FlexibleUpdateInstallStatus } =
          await import('@capawesome/capacitor-app-update');

        const info = await AppUpdate.getAppUpdateInfo();
        if (info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) return;

        // Start the flexible (non-blocking) update flow
        await AppUpdate.startFlexibleUpdate();

        // When download finishes, complete the update (shows Google Play restart prompt)
        listener = await AppUpdate.addListener('onFlexibleUpdateStateChange', async (state) => {
          if (state.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) {
            await AppUpdate.completeFlexibleUpdate();
          }
        });
      } catch (e) {
        // Never block the app — update check failure is silent
        console.log('In-app update check skipped:', e.message);
      }
    };

    checkUpdate();
    return () => { listener?.remove(); };
  }, []);
}

export default function App() {
  useInAppUpdate();
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}
